import { createServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import type { SupabaseClient } from "@supabase/supabase-js";

export interface RelatedBoxArticle {
  title: string;
  slug: string;
  box_slug: string;
  excerpt: string;
  hero_image_url?: string;
}

export const fetchRelatedBoxArticles = createServerFn({ method: "GET" })
  .inputValidator((data: { boxSlug: string; currentArticleSlug: string; limit?: number }) => data)
  .handler(async ({ data }): Promise<RelatedBoxArticle[]> => {
    const db = supabase as unknown as SupabaseClient;
    const limit = data.limit ?? 3;

    // First, try to get articles from the same box
    const { data: sameBoxArticles } = await db
      .from("home_box_articles")
      .select(`
        title,
        slug,
        excerpt,
        hero_image_url,
        home_boxes!inner (
          slug
        )
      `)
      .eq("home_boxes.slug", data.boxSlug)
      .eq("published", true)
      .neq("slug", data.currentArticleSlug)
      .limit(limit);

    const results: RelatedBoxArticle[] = (sameBoxArticles || []).map((a: any) => ({
      title: a.title,
      slug: a.slug,
      box_slug: a.home_boxes.slug,
      excerpt: a.excerpt,
      hero_image_url: a.hero_image_url,
    }));

    // Fallback: If we don't have enough, get from other boxes
    if (results.length < limit) {
      const { data: otherArticles } = await db
        .from("home_box_articles")
        .select(`
          title,
          slug,
          excerpt,
          hero_image_url,
          home_boxes!inner (
            slug
          )
        `)
        .neq("home_boxes.slug", data.boxSlug)
        .eq("published", true)
        .limit(limit - results.length);

      if (otherArticles) {
        results.push(...otherArticles.map((a: any) => ({
          title: a.title,
          slug: a.slug,
          box_slug: a.home_boxes.slug,
          excerpt: a.excerpt,
          hero_image_url: a.hero_image_url,
        })));
      }
    }

    return results;
  });
